import { useCallback, useMemo, useState } from 'react'
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts'
import { useObjectListAll } from '../api/object'
import { useObjectPropertyListAll } from '../api/object-properties'
import { useReportingUtilization } from '../api/reporting'
import { CurrencyFormatter } from '../components/currency-formatter'
import { Paper } from '../components/paper'
import { Select } from '../components/select'
import { getVehicleLabelText } from '../components/vehicle-formatter'

type SortKey = 'name' | 'occupiedDays' | 'reservationCount' | 'plannedRevenue' | 'adr'
type SortDir = 'asc' | 'desc'

export function VehicleUtilization({ range }: { range: { start: string; end: string } }) {
	const { data: vehicles = [] } = useObjectListAll('vehicle')
	const vehiclesMap = useMemo(() => new Map(vehicles.map(v => [v.id, v])), [vehicles])

	const [groupBy, setGroupBy] = useState<string>('id')

	const { data: properties = [] } = useObjectPropertyListAll('vehicle')
	const breakdownProperties = useMemo(
		() => properties.filter(p => p.type === 'single'),
		[properties],
	)
	const propertiesMap = useMemo(() => {
		return new Map(
			breakdownProperties.map(property => [
				property.identifier,
				new Map(property.options?.map(option => [option.id, option.name])),
			]),
		)
	}, [breakdownProperties])

	const breakdownProperty = useMemo(() => {
		return groupBy === 'id' ? vehiclesMap : (propertiesMap.get(groupBy) ?? new Map())
	}, [groupBy, vehiclesMap, propertiesMap])

	const utilizationQuery = useReportingUtilization({
		dateFrom: range.start,
		dateTo: range.end,
		groupBy,
	})
	const chartData = utilizationQuery.data ?? []

	const getLabel = useCallback(
		(id: string) => {
			if (groupBy === 'id') return getVehicleLabelText(vehiclesMap.get(id)) ?? id
			return (breakdownProperty as Map<string, string>).get(id) ?? 'nevyplněno'
		},
		[groupBy, vehiclesMap, breakdownProperty],
	)

	const breakdownPropertyOptions = useMemo(
		() => [
			{ label: 'Všechna vozidla', value: 'id' },
			...breakdownProperties.map(p => ({ label: p.name, value: p.identifier })),
		],
		[breakdownProperties],
	)

	const tableRows = useMemo(() => {
		return chartData?.map(row => {
			const adr =
				row.occupiedDays > 0 ? row.plannedRevenue / row.occupiedDays / row.vehicleCount : 0
			return {
				...row,
				name: getLabel(row.groupValue),
				adr,
			}
		})
	}, [chartData, getLabel])

	const [sortKey, setSortKey] = useState<SortKey>('name')
	const [sortDir, setSortDir] = useState<SortDir>('asc')

	const toggleSort = (key: SortKey) => {
		setSortKey(prevKey => {
			if (prevKey !== key) return key
			setSortDir(prevDir => (prevDir === 'asc' ? 'desc' : 'asc'))
			return key
		})
	}

	const sortedRows = useMemo(() => {
		return tableRows.toSorted((a, b) => {
			const va = a[sortKey]
			const vb = b[sortKey]

			if (typeof va === 'string' && typeof vb === 'string') {
				const cmp = va.localeCompare(vb, 'cs', { numeric: true })
				return sortDir === 'asc' ? cmp : -cmp
			}

			return sortDir === 'asc'
				? (va as number) - (vb as number)
				: (vb as number) - (va as number)
		})
	}, [tableRows, sortKey, sortDir])

	const totals = useMemo(() => {
		const sum = {
			occupiedDays: 0,
			reservationCount: 0,
			plannedRevenue: 0,
			adr: 0,
		}
		tableRows.forEach(r => {
			sum.occupiedDays += r.occupiedDays
			sum.reservationCount += r.reservationCount
			sum.plannedRevenue += r.plannedRevenue / r.vehicleCount
		})
		sum.adr = sum.occupiedDays > 0 ? sum.plannedRevenue / sum.occupiedDays : 0
		return sum
	}, [tableRows])

	return (
		<Paper className="p-4">
			<div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
				<h3 className="text-lg font-medium text-gray-900">Vytíženost vozidel</h3>
				<div className="flex flex-col sm:flex-row sm:items-center gap-2">
					<div className="flex items-center gap-2">
						Třídit podle:{' '}
						<Select
							options={breakdownPropertyOptions}
							selectedKey={groupBy}
							onSelectionChange={key => setGroupBy(key as string)}
						/>
					</div>
				</div>
			</div>

			<div className="h-96 mt-4">
				<ResponsiveContainer width="100%" height="100%">
					<BarChart
						data={chartData}
						layout="vertical"
						margin={{ top: 20, right: 30, left: 100, bottom: 5 }}
					>
						<CartesianGrid strokeDasharray="3 3" />
						<XAxis
							type="number"
							tick={{ fontSize: 12 }}
							domain={[0, 100]}
							label={{
								value: 'Vytíženost (%)',
								position: 'insideBottom',
								offset: -5,
							}}
						/>
						<YAxis
							type="category"
							dataKey="groupValue"
							tick={{ fontSize: 12 }}
							tickFormatter={getLabel}
							width={280}
						/>
						<Tooltip
							formatter={(value: number) => [`${value.toFixed(1)}%`, 'Vytíženost']}
							labelFormatter={getLabel}
						/>
						<Bar
							dataKey="utilizationPercentage"
							fill="#1c36c8"
							name="Vytíženost %"
							radius={[0, 4, 4, 0]}
						/>
					</BarChart>
				</ResponsiveContainer>
			</div>

			<div className="mt-6 overflow-auto">
				<table className="w-full text-sm">
					<thead className="bg-gray-50 text-gray-600">
						<tr>
							<SortHeader
								label="Vozidlo"
								col="name"
								toggleSort={toggleSort}
								sortKey={sortKey}
								sortDir={sortDir}
							/>
							<SortHeader
								label="Obsazené dny"
								col="occupiedDays"
								toggleSort={toggleSort}
								sortKey={sortKey}
								sortDir={sortDir}
							/>
							<SortHeader
								label="Počet rezervací"
								col="reservationCount"
								toggleSort={toggleSort}
								sortKey={sortKey}
								sortDir={sortDir}
							/>
							<SortHeader
								label="Tržby (plánované)"
								col="plannedRevenue"
								toggleSort={toggleSort}
								sortKey={sortKey}
								sortDir={sortDir}
							/>
							<SortHeader
								label="ADR"
								col="adr"
								align="text-right"
								toggleSort={toggleSort}
								sortKey={sortKey}
								sortDir={sortDir}
							/>
						</tr>
					</thead>
					<tbody className="divide-y divide-gray-200">
						{sortedRows.map(row => (
							<tr key={row.groupValue} className="hover:bg-gray-50">
								<td>
									<span className="font-medium">{row.name}</span>
								</td>
								<td>{row.occupiedDays}</td>
								<td>{row.reservationCount}</td>
								<td>
									<CurrencyFormatter value={row.plannedRevenue} />
								</td>
								<td>
									<CurrencyFormatter value={row.adr} />
								</td>
							</tr>
						))}
						{sortedRows.length === 0 ? (
							<tr>
								<td colSpan={5} className="px-3 py-6 text-center text-gray-500">
									Žádná vozidla k zobrazení.
								</td>
							</tr>
						) : (
							<tr className="font-semibold bg-gray-100">
								<td>Celkem</td>
								<td>{totals.occupiedDays}</td>
								<td>{totals.reservationCount}</td>
								<td>
									<CurrencyFormatter value={totals.plannedRevenue} />
								</td>
								<td>
									<CurrencyFormatter value={totals.adr} />
								</td>
							</tr>
						)}
					</tbody>
				</table>
			</div>
		</Paper>
	)
}

const SortHeader = ({
	toggleSort,
	sortKey,
	sortDir,
	label,
	col,
	align = 'text-left',
}: {
	toggleSort: (key: SortKey) => void
	sortKey: SortKey
	sortDir: SortDir
	label: string
	col: SortKey
	align?: 'text-left' | 'text-right'
}) => {
	const active = sortKey === col
	const arrow = active ? (sortDir === 'asc' ? '▲' : '▼') : ''
	const base = 'px-3 py-2 font-semibold cursor-pointer select-none'
	return (
		<th
			className={`${base} ${align}`}
			onClick={() => toggleSort(col)}
			title="Klikni pro řazení"
		>
			<span className={active ? 'text-gray-900' : 'text-gray-700'}>
				{label} {arrow && <span className="ml-1">{arrow}</span>}
			</span>
		</th>
	)
}
